import {
  ClerkLoaded,
  ClerkLoading,
  RedirectToSignIn,
  SignedIn,
  SignedOut,
  useOrganizationList,
  OrganizationSwitcher,
  UserButton,
  useUser,
  useClerk,
} from "@clerk/clerk-react";
import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ReactComponent as ClockIcon } from "../../assets/clock.svg";
import {
  HelpIcon,
  NavbarLogoIcon,
  NavbarPaymentIcon,
  NavbarPlanIcon,
  NavbarWorkspaceIcon,
  MessageIcon,
  PlanNavIcon,
  CouponIcon,
  NavbarHeadIcon,
  ShareLinkIcon,
  RightArrowEnterIcon,
  AddUserIcon,
  DiqiuIcon,
  NewDnaChatBotThinkIcon
} from "@/components/icons";
import { getMenuList } from "./config";
import {
  localStorageSaveUtmMediumAndSource,
  getIsYearPlanFromUrl,
  getIsSubscriptionFromUrl,
  isMobile,
} from "@/biz/tool";
import "./navbar.scss";
import { Timezone } from "../timezone";
import { usePaymentStore } from "@/hooks/usePaymentStore";
import { useModalStore } from "@/hooks/useModalStore";
import CustomOrganizationSwitcher from "./CustomOrganizationSwitcher";
import { useTenantStore } from "@/hooks/useTenant";
import { PaymentPlanEnum } from "@/pages/campaignCreate/components/budgetBidding/PricingPlan/config";
import { Button, message, Tooltip } from "antd";
import { useSessionListStore } from "@/hooks/useHeadChatBotStore";
import LanguageSelect from "@/components/LanguageSelect";
import { useBookDemoStatus } from "@/hooks/useBookDemoStatus";
import { useWhitelistStore } from "@/hooks/useWhitelistStore";

interface Props {
  isShowNav: boolean;
}

const Navbar: React.FC<Props> = ({ isShowNav }) => {
  // console.log("渲染就打印 - Navbar.tsx");
  const { t } = useTranslation();
  const { imNum } = useModalStore();
  const { paymentPlan, setPaymentPlan } = useTenantStore();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { shareTitle } = useSessionListStore();
  const showMenu =
   
    !pathname.includes("/wait") &&
   
    !pathname.includes("/share") &&
   
    !pathname.includes("/create-organization") &&
    !pathname.includes("/thank-you");
  const showShareBar = pathname.includes("/share");
  // 获取翻译后的菜单列表
  const menuList = getMenuList(t);

  useEffect(() => {
    // console.log("渲染就打印 - Navbar.tsx useeffect");
    // 存储 utm_medium
    // 存储 utm_source
    localStorageSaveUtmMediumAndSource();
    //
    // 存储 pc web 的 paymentPlan
    const isSubscription = getIsSubscriptionFromUrl();
    const isYear = getIsYearPlanFromUrl();
    if (isSubscription || isYear) {
      setPaymentPlan({
        isYear: isYear === "1",
        type:
          isSubscription === "1"
            ? PaymentPlanEnum.SUBSCRIPTION
            : PaymentPlanEnum.PAY_AS_YOU_GO,
      });
    }
  }, []);

  if (isMobile()) {
    return null;
  }

  return (
    <div className="navbar" style={{ display: isShowNav ? "flex" : "none" }}>
      <div className="navbar-content">
        <div className="flex justify-start items-center">
          <div className="flex items-center gap-2">
            <NewDnaChatBotThinkIcon />
          </div>
          {/* <div className="beta-version">{t('navbar.status.beta')}</div> */}
          <div className="ml-3">
            <ClerkLoaded>
              <ConditionalOrganizationSwitcher />
            </ClerkLoaded>
          </div>
        </div>
        {showMenu && (
          <div className="menu">
            {menuList?.map((item: any) => (
              <NavLink className="menu-item" to={item?.route} key={item?.label}>
                {({ isActive }) => {
                  const IconComponent = isActive ? item.ActiveIcon : item.Icon;
                  return (
                    <>
                      <IconComponent />
                      <span>{item.label}</span>
                    </>
                  );
                }}
              </NavLink>
            ))}
          </div>
        )}
        {/*  */}
        <div
          style={{
            display: showShareBar ? "flex" : "none",
          }}
          className="h-full flex-1 justify-center items-center text-[#19213d] text-base font-medium  leading-tight"
        >
          {shareTitle}
        </div>
        <div
          style={{
            display: showShareBar ? "flex" : "none",
          }}
          className="justify-between items-center"
        >
          <div className="inline-flex justify-start items-center gap-6">
            <Tooltip placement="bottom" title={t("navbar.tooltips.copyLink")}>
              <div
                onClick={() => {
                  function legacyCopy(text: string) {
                    const textarea = document.createElement("textarea");
                    textarea.value = text;
                    textarea.style.position = "fixed"; // 避免滚动跳转
                    document.body.appendChild(textarea);
                    textarea.select();

                    try {
                      const success = document.execCommand("copy");
                      if (success) {
                        message.success(t("navbar.messages.copySuccess"));
                      }
                    } catch (err) {
                      message.warning(t("navbar.messages.copyFail"));
                    } finally {
                      document.body.removeChild(textarea);
                    }
                  }
                  legacyCopy(window.location.href);
                }}
                className="p-2 bg-[#f0f0f3] rounded-[999px] flex justify-start items-center gap-2.5 overflow-hidden cursor-pointer"
              >
                <div className="flex justify-start items-center gap-1">
                  <ShareLinkIcon className="w-5 h-5 text-[#60646C] hover:text-[#121212]" />
                </div>
              </div>
            </Tooltip>

            <div className="flex justify-start items-center gap-4">
              <a
                href={`/login?redirect_url=/head`}
                className="cursor-pointer px-5 py-2 bg-[#f0f0f3] rounded-[999px] flex justify-center items-center gap-2.5"
              >
                <div className="text-center justify-center text-[#121212] text-sm font-semibold  leading-tight tracking-tight">
                  {t("navbar.actions.logIn")}
                </div>
              </a>
              <a
                href={`/signup?redirect_url=/head`}
                className="cursor-pointer px-4 py-2 bg-gradient-to-b from-[#3b3d3d] to-[#191a1a] rounded-[999px] shadow-[0px_0px_0px_1px_rgba(25,26,26,0.03)] shadow-[0px_1px_1px_-0.5px_rgba(25,26,26,0.03)] shadow-[0px_2px_2px_-1px_rgba(25,26,26,0.03)] shadow-[0px_4px_4px_-2px_rgba(25,26,26,0.02)] shadow-[0px_8px_8px_-4px_rgba(25,26,26,0.02)] shadow-[0px_16px_16px_-8px_rgba(25,26,26,0.02)] flex justify-start items-center gap-2"
              >
                <div className="text-center justify-center text-white text-sm font-semibold  leading-tight tracking-tight">
                  {t("navbar.actions.startForFree")}
                </div>
                <RightArrowEnterIcon className="w-[18px] h-[18px] text-white" />
              </a>
            </div>
          </div>
        </div>
        {/*  */}
        <div
          style={{
            display: showShareBar ? "none" : "block",
          }}
          className="avatar"
        >
          {/* <div className="help">
            <a href="mailto:support@aha.inc">
              <HelpIcon />
              <span>{t('navbar.userMenu.helpAndFeedback')}</span>
            </a>
          </div> */}
          <div className="flex items-center gap-2">
            {/* {showMenu && (
              <Button
                className='rounded-lg'
                onClick={() => setShareProductCodeModal(true)}
              >
                <div className="flex items-center justify-center">
                  <AddUserIcon className="w-4 h-4" />
                  <div className="ml-2 font-medium text-text-color-primary text-sm">{t('navbar.actions.invitationCode')}</div>
                </div>
              </Button>
            )} */}
            <div className="avatar-logo relative">
              <ClerkLoading>{/* add loading */}</ClerkLoading>
              {isShowNav && (
                <ClerkLoaded>
                  <SignedOut>
                    {/* <RedirectToSignIn /> */}
                    <RedirectToSignInPage />
                  </SignedOut>
                  <SignedIn>
                    <UserButtons showMenu={showMenu} />
                  </SignedIn>
                </ClerkLoaded>
              )}
              {imNum > 0 && (
                <div className="absolute right-[-12px] top-[-6px] w-[18px] h-[18px] rounded-full bg-[#ff4d4f] text-white text-[12px] items-center justify-center text-center">
                  {imNum > 99 ? "99+" : imNum}
                </div>
              )}
            </div>
          </div>
        </div>
        {/*  */}
      </div>
    </div>
  );
};

export default Navbar;

const UserButtons = ({ showMenu }: { showMenu: boolean }) => {
  const { t } = useTranslation();
  const { reQueryWalletData, walletDetail } = usePaymentStore();
  const {
    isDemoBooked,
    isNotICP,
    isLoading: isLoadingBookDemo,
    fetchBookDemoStatus,
  } = useBookDemoStatus();
  const { userMemberships } = useOrganizationList({
    userMemberships: true,
  });
  const navigate = useNavigate();
  const { setShowImModal, imNum, setImNum } = useModalStore();
  const { user } = useUser();
  const clerk = useClerk();
  const {
    setActive,
    userMemberships: { isLoading, count, data },
  } = useOrganizationList({
    userMemberships: {
      infinite: true,
    },
  });

  useEffect(() => {
    reQueryWalletData();
    const userEmail = user?.emailAddresses?.[0]?.emailAddress;
    if (userEmail) {
      fetchBookDemoStatus(userEmail);
    }
  }, []);

  // 处理跳转问题
  useEffect(() => {
    if (setActive && !isLoading && clerk.loaded) {
      if (!isLoadingBookDemo) {
        // if (isNotICP) {
        //   navigate("/thank-you");
        //   return;
        // }
        if (!isDemoBooked && count === 0) {
          navigate("/book-demo");
          return;
        }
        if (count === 0) {
          navigate("/create-organization");
          return;
        }
      }
    }
  }, [
    isDemoBooked,
    isNotICP,
    isLoadingBookDemo,
    isLoading,
    clerk.loaded,
    user?.id,
  ]);

  if (userMemberships.isLoading) {
    // {t('navbar.messages.loading')} // pending a loading animation
    return <div />;
  }

  return (
    <UserButton
      appearance={{
        elements: {
          avatarBox: "h-10 w-10",
        },
      }}
    >
      <UserButton.UserProfilePage
        label={t("navbar.userMenu.timezone")}
        url="timezone"
        labelIcon={<ClockIcon />}
      >
        <Timezone />
      </UserButton.UserProfilePage>
      <UserButton.UserProfilePage
        label={t("navbar.userMenu.language")}
        url="language"
        labelIcon={<DiqiuIcon />}
      >
        <LanguageSelect />
      </UserButton.UserProfilePage>
      {showMenu && (
        <UserButton.MenuItems>
          <UserButton.Action
            label={t("navbar.userMenu.billingAndPayments")}
            onClick={() => {
              if (window.location.pathname.includes("/organization")) {
                window.location.hash = "#/payment";
              } else {
                navigate("/organization#/payment");
              }
            }}
            labelIcon={<NavbarPaymentIcon />}
          />
          {/* {Boolean(
            walletDetail &&
              // 下面两个租户隐藏Plan
              walletDetail?.clerkOrgId !== "org_2st3MgHliOJWuODBKGMLVZM1zsI" && // Shakker AI
              walletDetail?.clerkOrgId !== "org_2qxpeVzJgjLAaour5G01NTeI0jA" // junoverse
          ) && (
            <UserButton.Action
              label={t("navbar.userMenu.plan")}
              onClick={() => {
                if (window.location.pathname.includes("/organization")) {
                  window.location.hash = "#/plan";
                } else {
                  navigate("/organization#/plan");
                }
              }}
              labelIcon={<NavbarPlanIcon />}
            />
          )} */}

          <UserButton.Action
            label={t("navbar.userMenu.couponCode")}
            onClick={() => {
              if (window.location.pathname.includes("/organization")) {
                window.location.hash = "#/coupon";
              } else {
                navigate("/organization#/coupon");
              }
            }}
            labelIcon={<CouponIcon />}
          />
          <UserButton.Action
            label={t("navbar.userMenu.workspace")}
            onClick={() => {
              if (window.location.pathname.includes("/organization")) {
                // navigate("#/organization-members");
                window.location.hash = "#/organization-members";
              } else {
                navigate("/organization#/organization-members");
              }
            }}
            labelIcon={<NavbarWorkspaceIcon />}
          />
          {/* <UserButton.Action
            label={t("navbar.userMenu.messages")}
            onClick={() => {
              setShowImModal(true);
              setImNum(0);
            }}
            labelIcon={
              imNum > 0 ? (
                <div className="relative">
                  <MessageIcon />
                  <div className="absolute right-[-12px] top-[-10px] w-[18px] h-[18px] rounded-full bg-[#ff4d4f] text-[12px] items-center justify-center">
                    {imNum > 99 ? "99+" : imNum}
                  </div>
                </div>
              ) : (
                <MessageIcon />
              )
            }
          /> */}
        </UserButton.MenuItems>
      )}
    </UserButton>
  );
};

const ConditionalOrganizationSwitcher = () => {
  const {
    userMemberships: { isLoading, count },
  } = useOrganizationList({
    userMemberships: {
      pageSize: 99999,
    },
  });

  if (isLoading) {
    return <div />;
  }

  if (count && count > 1) {
    return (
      <div className="flex items-center gap-2">
        {/* <OrganizationSwitcher hidePersonal afterSelectOrganizationUrl={"/"} /> */}
        <CustomOrganizationSwitcher />
      </div>
    );
  }

  return <div />;
};

const RedirectToSignInPage = () => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // if (!mounted) return;
    const thisPageUrl = window.location.href;

    if (
      thisPageUrl.includes("organization") ||
      thisPageUrl.includes("login") ||
      thisPageUrl.includes("signup") ||
      thisPageUrl.includes("share")
    ) {
      //
    } else {
      const redirectUrl = thisPageUrl.replace("organization", "");
      window.location.href = "/login?redirect_url=" + redirectUrl;
    }
  }, []);

  useEffect(() => {
    // setMounted(true);
  }, []);

  return null;
};
